import {ReactNode} from 'react';

import {DocumentField, DocumentInputField, DocumentUIField, DocumentValue} from '@/engine';
import {INPUT_FIELD_TYPES, UI_FIELD_TYPES} from '@/model/types';
import {isFieldInputType, isFieldUIType} from '@/model/utils/fields';
import * as FIELDS from './fields';

type InputFieldProps = {
  value: string;
  storedValue: DocumentValue | DocumentValue[];
  handleChange: (value: DocumentValue | DocumentValue[], validate?: boolean) => void;
  handleBlur: () => void;
  handleFocus: () => void;
  handleClick: () => void;
  field: DocumentInputField;
};

type UIFieldProps = {
  field: DocumentUIField;
};

export type FieldProps<T extends DocumentField> = T extends DocumentInputField ? InputFieldProps : UIFieldProps;

type FieldComponent<T extends DocumentField> = (props: FieldProps<T>) => ReactNode;

export const MetaField = <T extends DocumentField>(props: FieldProps<T>) => {
  if (isFieldInputType(props.field)) {
    const Comp = getInputFieldComp(props.field);
    return <Comp {...(props as FieldProps<DocumentInputField>)} />;
  }

  if (isFieldUIType(props.field)) {
    const Comp = getUIFieldComp(props.field);
    return <Comp {...(props as FieldProps<DocumentUIField>)} />;
  }

  // @ts-expect-error -- This is a catch-all for any other field types
  throw new Error(`Unknown field type: ${props.field.type}`);
};

const getUIFieldComp = (field: DocumentUIField): FieldComponent<DocumentUIField> => {
  switch (field.type) {
    case UI_FIELD_TYPES.MESSAGE:
      return FIELDS.MessageField;

    case UI_FIELD_TYPES.SUB_SECTION_TITLE:
      return FIELDS.SubsectionTitleField;

    case UI_FIELD_TYPES.TABLE:
      return FIELDS.TableField;

    case UI_FIELD_TYPES.SPACER:
      return FIELDS.SpacerField;

    case UI_FIELD_TYPES.DIVIDER:
      return FIELDS.DividerField;

    default:
      throw new Error(`Unknown UI field type: ${field.type}`);
  }
};

const getInputFieldComp = (field: DocumentInputField): FieldComponent<DocumentInputField> => {
  switch (field.type) {
    case INPUT_FIELD_TYPES.CURRENCY:
      return FIELDS.CurrencyField;

    case INPUT_FIELD_TYPES.TEXT:
    case INPUT_FIELD_TYPES.DATE:
    case INPUT_FIELD_TYPES.EMAIL:
    case INPUT_FIELD_TYPES.NUMBER:
    case INPUT_FIELD_TYPES.TEL:
      return field.mask ? FIELDS.MaskedInputField : FIELDS.InputField;

    case INPUT_FIELD_TYPES.PERCENTAGE:
      return FIELDS.PercentageField;

    case INPUT_FIELD_TYPES.HIDDEN:
      return FIELDS.HiddenField;

    case INPUT_FIELD_TYPES.CHECKBOX:
    case INPUT_FIELD_TYPES.RADIO:
      return field.options?.length ? FIELDS.ChoiceField : FIELDS.BooleanField;

    case INPUT_FIELD_TYPES.SELECT:
      return FIELDS.SelectField;

    case INPUT_FIELD_TYPES.COMBOBOX:
      return FIELDS.ComboboxField;

    case INPUT_FIELD_TYPES.FILE:
      return FIELDS.FileField;

    case INPUT_FIELD_TYPES.BUTTON:
      return FIELDS.ButtonField;

    default:
      throw new Error(`Unknown form field type: ${field.type}`);
  }
};
